import Collapse from 'antd/lib/collapse'
import Tag from 'antd/lib/tag'
import { TaskMode } from 'Types/Tasks.types'
import { useTaskContext } from 'Contexts/TaskContext'
import Flex from 'antd/lib/flex'
import AIGenerateIcon from 'Assets/icons/generate.svg'
import CursorIcon from 'Assets/icons/cursor.svg'
import Progress from 'antd/lib/progress'
import { getJSONPathFromPath } from 'Utils/attributes.utils'
import Skeleton from 'antd/lib/skeleton'
import Spin from 'antd/lib/spin'
import SyncOutlined from '@ant-design/icons/lib/icons/SyncOutlined'
import { Base64Image } from 'Components/common/Base64Image/Base64Image'
import { S3Link } from 'Components/common/S3Link/S3Link'
import MarkdownRenderer from 'Components/common/MarkdownRenderer/MarkdownRenderer'
import { isValidMarkdown } from 'Utils/markdown.util'
import { getIconBasedOnCompletionPercentage } from 'Utils/component.utils'
import { JSONObject } from 'Types/Common.types'
import Icon from '@ant-design/icons/lib/components/Icon'
import ErrorIcon from 'Assets/icons/error-diamond-icon.svg?react'
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined'
import { useState, useEffect } from 'react'

type ModelContent = {
    mode: TaskMode
    data: JSONObject
    title: string
    path: string
    nesting: number
    isReadOnly?: boolean
    getAttributeFilledPercentageByPath: (path: string) => { percent: number } | null
    turnIndex: number
    isLoading: boolean
    isResponseStreaming?: boolean
    streamingPath?: string | null
    isLatestTurn: boolean
    isRegenerationAllowed: boolean
    onClickRegenerateContent?: () => void
    isPreviousTurn?: boolean
    errorMessage?: string | null
}

const STREAMING_MESSAGES = ['Generating response...', 'You can still expand and check for content']

export const ModelContent = ({
    data,
    title,
    path,
    mode,
    nesting,
    isReadOnly = false,
    getAttributeFilledPercentageByPath,
    turnIndex,
    isLoading,
    isResponseStreaming = false,
    streamingPath = null,
    isRegenerationAllowed,
    onClickRegenerateContent,
    isLatestTurn,
    isPreviousTurn,
    errorMessage,
}: ModelContent) => {
    const taskContext = useTaskContext()
    const attributeManager = taskContext.getAttributeManager?.()

    const [currentMessageIndex, setCurrentMessageIndex] = useState(0)

    useEffect(() => {
        if (isResponseStreaming && isLatestTurn && !isPreviousTurn) {
            const interval = setInterval(() => {
                setCurrentMessageIndex(prev => (prev + 1) % STREAMING_MESSAGES.length)
            }, 5000)

            return () => clearInterval(interval)
        } else {
            setCurrentMessageIndex(0)
        }
    }, [isResponseStreaming, isLatestTurn, isPreviousTurn])

    const isStreamingAtPath = (path: string) => {
        const isStreamingInLatestTurn = isResponseStreaming && isLatestTurn && !isPreviousTurn

        return !!(isStreamingInLatestTurn && streamingPath && streamingPath === path)
    }

    const renderValue = (value: string | number | boolean | undefined | null, key?: string) => {
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            /*
             * These are based on models we have tested as of now. Once we test on more models, we can add more exceptions here.
             * 1. b64_json -> Base64Image
             * 2. url (starts with expertsvc/model_conv_v2/) -> S3 Link to download the file.
             * 3. markdown -> Use MarkdownRenderer to render the markdown content.
             */
            if (key === 'b64_json' && typeof value === 'string') {
                return <Base64Image base64={value} />
            }
            if (
                key === 'url' &&
                typeof value === 'string' &&
                value.startsWith('expertsvc/model_conv_v2/')
            ) {
                return <S3Link url={value} />
            }
            if (typeof value === 'string' && isValidMarkdown(value)) {
                return (
                    <div>
                        <MarkdownRenderer children={value} escapeBackslashses={false} />
                    </div>
                )
            }
            return <div className="label-value">{value}</div>
        }
        if (value === null || value === undefined) {
            return <Tag color="gray">null</Tag>
        }
        return null
    }

    const attributeFilledPercentage = getAttributeFilledPercentageByPath(
        getJSONPathFromPath(path, turnIndex)
    )

    if (typeof data !== 'object' || data === null) {
        return (
            <div style={{ marginLeft: 16 }}>
                <div className={`title ${isStreamingAtPath(path) ? 'streaming-text-fade' : ''}`}>
                    {title}:
                </div>{' '}
                {renderValue(data, undefined)}
                {
                    attributeManager
                        ?.getAttributeGroup(getJSONPathFromPath(path, turnIndex))
                        ?.getElement()?.element
                }
            </div>
        )
    }

    const isArray = Array.isArray(data)

    const children = Object.entries(data).map(([key, value], index) => {
        const childTitle = isArray ? `${index + 1}` : key
        const childPath = isArray ? `${path}[${key}]` : `${path}.${key}`
        const isStreamingAtChildPath = isStreamingAtPath(childPath)

        if (typeof value === 'object' && value !== null) {
            return (
                <div
                    key={childPath}
                    className={isStreamingAtChildPath ? 'model-content-streaming-fade' : ''}
                >
                    <ModelContent
                        data={value as JSONObject}
                        title={childTitle}
                        path={childPath}
                        mode={mode}
                        nesting={nesting + 1}
                        isReadOnly={isReadOnly}
                        getAttributeFilledPercentageByPath={getAttributeFilledPercentageByPath}
                        turnIndex={turnIndex}
                        isLoading={isLoading}
                        isResponseStreaming={isResponseStreaming}
                        streamingPath={streamingPath}
                        isLatestTurn={isLatestTurn}
                        isRegenerationAllowed={isRegenerationAllowed}
                        onClickRegenerateContent={onClickRegenerateContent}
                        isPreviousTurn={isPreviousTurn}
                    />
                </div>
            )
        } else {
            const attributeFilledPercentage = getAttributeFilledPercentageByPath(
                getJSONPathFromPath(childPath, turnIndex)
            )

            const childAttributeFilledStatus =
                getIconBasedOnCompletionPercentage(attributeFilledPercentage)

            return (
                <Collapse
                    key={childPath}
                    expandIconPosition="start"
                    ghost={true}
                    items={[
                        {
                            key: path,
                            label: (
                                <div className="model-content-collapse-header">
                                    <div
                                        className={`title ${
                                            isStreamingAtChildPath ? 'streaming-text-fade' : ''
                                        }`}
                                    >
                                        {childTitle}
                                    </div>
                                    {childAttributeFilledStatus}
                                </div>
                            ),
                            children: (
                                <>
                                    {renderValue(value, key)}
                                    {
                                        attributeManager
                                            ?.getAttributeGroup(
                                                getJSONPathFromPath(childPath, turnIndex)
                                            )
                                            ?.getElement()?.element
                                    }
                                </>
                            ),
                            className: `model-content-collapse-panel`,
                            forceRender: true,
                        },
                    ]}
                />
            )
        }
    })

    if (path === '$') {
        return (
            <Flex gap={10} className="model-content-root" justify="flex-start" align="flex-start">
                <img
                    src={AIGenerateIcon}
                    alt="Generate Icon"
                    className="model-content-generate-icon"
                />
                {isLoading && isLatestTurn ? (
                    <div className="model-content-skeleton-container">
                        <Skeleton active={true} paragraph={{ rows: 3 }} />
                    </div>
                ) : (
                    <>
                        <div className="model-content-container">
                            {isPreviousTurn ? null : (
                                <Flex vertical>
                                    {isResponseStreaming && isLatestTurn ? (
                                        <Flex
                                            gap={4}
                                            align="center"
                                            className="model-content-streaming-container"
                                        >
                                            <Spin
                                                size="small"
                                                indicator={
                                                    <LoadingOutlined className="model-content-streaming-icon" />
                                                }
                                            />
                                            <p className="model-content-instructions">
                                                {STREAMING_MESSAGES[currentMessageIndex]}
                                            </p>
                                        </Flex>
                                    ) : (
                                        <>
                                            <Flex gap={4}>
                                                <img
                                                    src={CursorIcon}
                                                    alt="Cursor Icon"
                                                    className="model-content-cursor-icon"
                                                />
                                                <p className="model-content-instructions">
                                                    Open and fill the attributes
                                                </p>
                                            </Flex>
                                            <Progress
                                                percent={attributeFilledPercentage?.percent}
                                                showInfo={false}
                                                className="model-content-progress"
                                                size={{ width: 350, height: 4 }}
                                                strokeColor={'#7C53A5'}
                                                trailColor={'#E3E6E8'}
                                            />
                                        </>
                                    )}
                                </Flex>
                            )}
                            <div
                                key={path}
                                className={`model-content root-content ${
                                    errorMessage ? 'model-content-error' : ''
                                }`}
                            >
                                {children}
                                {
                                    attributeManager
                                        ?.getAttributeGroup(getJSONPathFromPath(path, turnIndex))
                                        ?.getElement()?.element
                                }
                            </div>
                            <Flex
                                className="model-content-regenerate-container"
                                justify="space-between"
                            >
                                {errorMessage && (
                                    <div className="model-content-error-text">
                                        <Icon component={ErrorIcon} />
                                        {errorMessage}
                                    </div>
                                )}
                                {isRegenerationAllowed && (
                                    <Flex
                                        className="regenerate-container"
                                        gap={5}
                                        onClick={onClickRegenerateContent}
                                    >
                                        <SyncOutlined />
                                        <span>REGENERATE</span>
                                    </Flex>
                                )}
                            </Flex>
                        </div>
                    </>
                )}
            </Flex>
        )
    }

    return (
        <Collapse
            data-testid={`dynamic-collapse-${path}`}
            expandIconPosition="start"
            ghost={true}
            defaultActiveKey={nesting <= 2 ? path : undefined}
            items={[
                {
                    key: path,
                    label: (
                        <div className="model-content-collapse-header">
                            <div
                                className={`title ${
                                    isStreamingAtPath(path) ? 'streaming-text-fade' : ''
                                }`}
                            >
                                {title}
                            </div>
                            {getIconBasedOnCompletionPercentage(attributeFilledPercentage)}
                        </div>
                    ),
                    children: (
                        <>
                            {children}
                            {
                                attributeManager
                                    ?.getAttributeGroup(getJSONPathFromPath(path, turnIndex))
                                    ?.getElement()?.element
                            }
                        </>
                    ),
                    className: `model-content-collapse-panel`,
                    forceRender: true,
                },
            ]}
        />
    )
}
